import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text, Box, Sphere, Cylinder } from '@react-three/drei';
import * as THREE from 'three';

interface DanceMove {
  id: string;
  name: string;
  emoji: string;
  duration: number;
  description: string;
}

interface Robot {
  id: string;
  name: string;
  color: string;
  moves: DanceMove[];
  score: number;
  isActive: boolean;
}

// 3D Robot Component with Articulated Body Parts
const Robot3D: React.FC<{
  position: [number, number, number];
  color: string;
  isDancing: boolean;
  danceAnimation?: string;
  name: string;
}> = ({ position, color, isDancing, danceAnimation, name }) => {
  const robotRef = useRef<THREE.Group>(null);
  const headRef = useRef<THREE.Group>(null);
  const leftArmRef = useRef<THREE.Group>(null);
  const rightArmRef = useRef<THREE.Group>(null);
  const leftLegRef = useRef<THREE.Group>(null);
  const rightLegRef = useRef<THREE.Group>(null);
  const leftShoulderRef = useRef<THREE.Group>(null);
  const rightShoulderRef = useRef<THREE.Group>(null);
  const leftHipRef = useRef<THREE.Group>(null);
  const rightHipRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    if (isDancing && robotRef.current) {
      const time = state.clock.elapsedTime;
      
      // Base robot movement
      robotRef.current.position.y = position[1] + Math.sin(time * 2) * 0.1;
      
      switch (danceAnimation) {
        case 'robot-wave':
          // Smooth head bobbing
          if (headRef.current) {
            headRef.current.rotation.x = Math.sin(time * 3) * 0.15;
            headRef.current.rotation.y = Math.sin(time * 2.5) * 0.3;
            headRef.current.rotation.z = Math.sin(time * 1.5) * 0.1;
          }
          // Fluid left arm waving
          if (leftArmRef.current) {
            leftArmRef.current.rotation.x = Math.sin(time * 4) * 1.2;
            leftArmRef.current.rotation.z = Math.sin(time * 3) * 0.5;
            leftArmRef.current.rotation.y = Math.sin(time * 2) * 0.3;
          }
          // Right arm complementary movement
          if (rightArmRef.current) {
            rightArmRef.current.rotation.x = Math.sin(time * 2.5) * 0.4;
            rightArmRef.current.rotation.z = Math.sin(time * 1.8) * 0.2;
          }
          // Hip sway
          if (leftHipRef.current) {
            leftHipRef.current.rotation.y = Math.sin(time * 2) * 0.2;
          }
          if (rightHipRef.current) {
            rightHipRef.current.rotation.y = Math.sin(time * 2 + Math.PI) * 0.2;
          }
          break;
          
        case 'robot-spin':
          // Smooth full body spin
          robotRef.current.rotation.y += 0.08;
          robotRef.current.scale.setScalar(1 + Math.sin(time * 3) * 0.15);
          // Fluid arm spinning
          if (leftArmRef.current) {
            leftArmRef.current.rotation.x = Math.sin(time * 6) * 0.8;
            leftArmRef.current.rotation.z = Math.sin(time * 4) * 0.4;
          }
          if (rightArmRef.current) {
            rightArmRef.current.rotation.x = Math.sin(time * 6 + Math.PI) * 0.8;
            rightArmRef.current.rotation.z = Math.sin(time * 4 + Math.PI) * 0.4;
          }
          // Leg movement during spin
          if (leftLegRef.current) {
            leftLegRef.current.rotation.x = Math.sin(time * 5) * 0.3;
          }
          if (rightLegRef.current) {
            rightLegRef.current.rotation.x = Math.sin(time * 5 + Math.PI) * 0.3;
          }
          break;
          
        case 'robot-shuffle':
          // Smooth side-to-side movement
          robotRef.current.position.x = position[0] + Math.sin(time * 4) * 0.4;
          robotRef.current.rotation.z = Math.sin(time * 3) * 0.3;
          robotRef.current.rotation.y = Math.sin(time * 2) * 0.1;
          // Fluid leg stepping
          if (leftLegRef.current) {
            leftLegRef.current.rotation.x = Math.sin(time * 5) * 0.5;
            leftLegRef.current.rotation.z = Math.sin(time * 3) * 0.2;
          }
          if (rightLegRef.current) {
            rightLegRef.current.rotation.x = Math.sin(time * 5 + Math.PI) * 0.5;
            rightLegRef.current.rotation.z = Math.sin(time * 3 + Math.PI) * 0.2;
          }
          // Coordinated arm swinging
          if (leftArmRef.current) {
            leftArmRef.current.rotation.z = Math.sin(time * 4) * 0.6;
            leftArmRef.current.rotation.x = Math.sin(time * 2) * 0.3;
          }
          if (rightArmRef.current) {
            rightArmRef.current.rotation.z = Math.sin(time * 4 + Math.PI) * 0.6;
            rightArmRef.current.rotation.x = Math.sin(time * 2 + Math.PI) * 0.3;
          }
          // Hip movement
          if (leftHipRef.current) {
            leftHipRef.current.rotation.y = Math.sin(time * 4) * 0.3;
          }
          if (rightHipRef.current) {
            rightHipRef.current.rotation.y = Math.sin(time * 4 + Math.PI) * 0.3;
          }
          break;
          
        case 'robot-jump':
          // Dynamic jumping motion
          robotRef.current.position.y = position[1] + Math.abs(Math.sin(time * 5)) * 0.6;
          robotRef.current.rotation.x = Math.sin(time * 5) * 0.1;
          // Fluid arm movement during jump
          if (leftArmRef.current) {
            leftArmRef.current.rotation.x = Math.sin(time * 5) * 0.8;
            leftArmRef.current.rotation.z = Math.sin(time * 3) * 0.3;
          }
          if (rightArmRef.current) {
            rightArmRef.current.rotation.x = Math.sin(time * 5) * 0.8;
            rightArmRef.current.rotation.z = Math.sin(time * 3 + Math.PI) * 0.3;
          }
          // Coordinated leg tucking
          if (leftLegRef.current) {
            leftLegRef.current.rotation.x = Math.sin(time * 5) * 0.6;
            leftLegRef.current.rotation.z = Math.sin(time * 2) * 0.2;
          }
          if (rightLegRef.current) {
            rightLegRef.current.rotation.x = Math.sin(time * 5) * 0.6;
            rightLegRef.current.rotation.z = Math.sin(time * 2 + Math.PI) * 0.2;
          }
          // Hip movement for balance
          if (leftHipRef.current) {
            leftHipRef.current.rotation.y = Math.sin(time * 4) * 0.2;
          }
          if (rightHipRef.current) {
            rightHipRef.current.rotation.y = Math.sin(time * 4 + Math.PI) * 0.2;
          }
          break;
          
        case 'robot-moonwalk':
          // Smooth backward sliding motion
          robotRef.current.position.z = position[2] - Math.sin(time * 3) * 0.5;
          robotRef.current.rotation.y = Math.sin(time * 2) * 0.1;
          // Leg movement for moonwalk
          if (leftLegRef.current) {
            leftLegRef.current.rotation.x = Math.sin(time * 4) * 0.4;
            leftLegRef.current.rotation.z = Math.sin(time * 3) * 0.3;
          }
          if (rightLegRef.current) {
            rightLegRef.current.rotation.x = Math.sin(time * 4 + Math.PI) * 0.4;
            rightLegRef.current.rotation.z = Math.sin(time * 3 + Math.PI) * 0.3;
          }
          // Arms in moonwalk pose
          if (leftArmRef.current) {
            leftArmRef.current.rotation.x = Math.sin(time * 2) * 0.3;
            leftArmRef.current.rotation.z = Math.sin(time * 1.5) * 0.2;
          }
          if (rightArmRef.current) {
            rightArmRef.current.rotation.x = Math.sin(time * 2 + Math.PI) * 0.3;
            rightArmRef.current.rotation.z = Math.sin(time * 1.5 + Math.PI) * 0.2;
          }
          break;
          
        case 'robot-breakdance':
          // Complex floor moves
          robotRef.current.rotation.y += 0.15;
          robotRef.current.rotation.x = Math.sin(time * 4) * 0.3;
          robotRef.current.rotation.z = Math.sin(time * 3) * 0.2;
          // Dynamic arm movements
          if (leftArmRef.current) {
            leftArmRef.current.rotation.x = Math.sin(time * 6) * 1.2;
            leftArmRef.current.rotation.z = Math.sin(time * 5) * 0.8;
            leftArmRef.current.rotation.y = Math.sin(time * 4) * 0.5;
          }
          if (rightArmRef.current) {
            rightArmRef.current.rotation.x = Math.sin(time * 6 + Math.PI) * 1.2;
            rightArmRef.current.rotation.z = Math.sin(time * 5 + Math.PI) * 0.8;
            rightArmRef.current.rotation.y = Math.sin(time * 4 + Math.PI) * 0.5;
          }
          // Leg movements
          if (leftLegRef.current) {
            leftLegRef.current.rotation.x = Math.sin(time * 5) * 0.8;
            leftLegRef.current.rotation.z = Math.sin(time * 4) * 0.6;
          }
          if (rightLegRef.current) {
            rightLegRef.current.rotation.x = Math.sin(time * 5 + Math.PI) * 0.8;
            rightLegRef.current.rotation.z = Math.sin(time * 4 + Math.PI) * 0.6;
          }
          break;
          
        default:
          // Smooth idle animation
          if (headRef.current) {
            headRef.current.rotation.y = Math.sin(time * 0.8) * 0.15;
            headRef.current.rotation.x = Math.sin(time * 0.6) * 0.05;
          }
          if (leftArmRef.current) {
            leftArmRef.current.rotation.x = Math.sin(time * 1.2) * 0.15;
            leftArmRef.current.rotation.z = Math.sin(time * 0.8) * 0.1;
          }
          if (rightArmRef.current) {
            rightArmRef.current.rotation.x = Math.sin(time * 1.2 + Math.PI) * 0.15;
            rightArmRef.current.rotation.z = Math.sin(time * 0.8 + Math.PI) * 0.1;
          }
          if (leftLegRef.current) {
            leftLegRef.current.rotation.x = Math.sin(time * 0.9) * 0.1;
          }
          if (rightLegRef.current) {
            rightLegRef.current.rotation.x = Math.sin(time * 0.9 + Math.PI) * 0.1;
          }
          break;
      }
    } else if (robotRef.current) {
      // Reset to default position
      robotRef.current.position.set(position[0], position[1], position[2]);
      robotRef.current.rotation.set(0, 0, 0);
      robotRef.current.scale.setScalar(1);
      
      if (headRef.current) {
        headRef.current.rotation.set(0, 0, 0);
      }
      if (leftArmRef.current) {
        leftArmRef.current.rotation.set(0, 0, 0);
      }
      if (rightArmRef.current) {
        rightArmRef.current.rotation.set(0, 0, 0);
      }
      if (leftLegRef.current) {
        leftLegRef.current.rotation.set(0, 0, 0);
      }
      if (rightLegRef.current) {
        rightLegRef.current.rotation.set(0, 0, 0);
      }
    }
  });

  return (
    <group ref={robotRef} position={position}>
      {/* Robot Torso - More humanoid shape */}
      <Box args={[0.7, 1.0, 0.5]} position={[0, 0, 0]}>
        <meshStandardMaterial color={color} metalness={0.7} roughness={0.3} />
      </Box>
      
      {/* Chest Panel */}
      <Box args={[0.6, 0.3, 0.1]} position={[0, 0.2, 0.3]}>
        <meshStandardMaterial color="#333333" metalness={0.9} roughness={0.1} />
      </Box>
      
      {/* Robot Head with articulated movement */}
      <group ref={headRef} position={[0, 0.8, 0]}>
        {/* Main Head - More rounded */}
        <Sphere args={[0.4, 16, 16]} position={[0, 0, 0]}>
          <meshStandardMaterial color={color} metalness={0.8} roughness={0.2} />
        </Sphere>
        
        {/* Head Details */}
        <Box args={[0.3, 0.1, 0.05]} position={[0, 0.2, 0.35]}>
          <meshStandardMaterial color="#666666" metalness={0.8} roughness={0.2} />
        </Box>
        
        {/* Robot Eyes - More prominent */}
        <Sphere args={[0.08, 12, 12]} position={[-0.15, 0.05, 0.35]}>
          <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={0.8} />
        </Sphere>
        <Sphere args={[0.08, 12, 12]} position={[0.15, 0.05, 0.35]}>
          <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={0.8} />
        </Sphere>
        
        {/* Eye Glow Effect */}
        <Sphere args={[0.12, 8, 8]} position={[-0.15, 0.05, 0.37]}>
          <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={0.3} transparent opacity={0.3} />
        </Sphere>
        <Sphere args={[0.12, 8, 8]} position={[0.15, 0.05, 0.37]}>
          <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={0.3} transparent opacity={0.3} />
        </Sphere>
        
        {/* Robot Antenna */}
        <Cylinder args={[0.03, 0.03, 0.25]} position={[0, 0.45, 0]} rotation={[0, 0, 0]}>
          <meshStandardMaterial color="#ffff00" />
        </Cylinder>
        <Sphere args={[0.06, 8, 8]} position={[0, 0.57, 0]}>
          <meshStandardMaterial color="#ffff00" emissive="#ffff00" emissiveIntensity={0.5} />
        </Sphere>
        
        {/* Mouth/Expression */}
        <Box args={[0.2, 0.05, 0.02]} position={[0, -0.1, 0.35]}>
          <meshStandardMaterial color="#333333" />
        </Box>
      </group>
      
      {/* Left Shoulder Joint */}
      <group ref={leftShoulderRef} position={[-0.4, 0.3, 0]}>
        <Sphere args={[0.1, 8, 8]} position={[0, 0, 0]}>
          <meshStandardMaterial color="#333333" metalness={0.9} roughness={0.1} />
        </Sphere>
        
        {/* Left Arm with articulated movement */}
        <group ref={leftArmRef} position={[0, -0.2, 0]}>
          {/* Upper Arm - More cylindrical */}
          <Cylinder args={[0.08, 0.08, 0.5]} position={[0, -0.25, 0]} rotation={[0, 0, Math.PI/2]}>
            <meshStandardMaterial color={color} metalness={0.7} roughness={0.3} />
          </Cylinder>
          
          {/* Arm Details */}
          <Box args={[0.1, 0.3, 0.05]} position={[0, -0.1, 0.08]}>
            <meshStandardMaterial color="#444444" metalness={0.8} roughness={0.2} />
          </Box>
          
          {/* Elbow Joint */}
          <Sphere args={[0.1, 12, 12]} position={[0, -0.5, 0]}>
            <meshStandardMaterial color="#333333" metalness={0.9} roughness={0.1} />
          </Sphere>
          
          {/* Forearm - More cylindrical */}
          <Cylinder args={[0.06, 0.06, 0.4]} position={[0, -0.7, 0]} rotation={[0, 0, Math.PI/2]}>
            <meshStandardMaterial color={color} metalness={0.7} roughness={0.3} />
          </Cylinder>
          
          {/* Wrist Joint */}
          <Sphere args={[0.08, 8, 8]} position={[0, -0.9, 0]}>
            <meshStandardMaterial color="#333333" metalness={0.9} roughness={0.1} />
          </Sphere>
          
          {/* Hand - More detailed with proper fingers */}
          <Box args={[0.1, 0.15, 0.2]} position={[0, -1.0, 0]}>
            <meshStandardMaterial color="#666666" metalness={0.8} roughness={0.2} />
          </Box>
          
          {/* Thumb */}
          <Box args={[0.03, 0.06, 0.03]} position={[-0.05, -0.95, 0.05]} rotation={[0, 0, Math.PI/4]}>
            <meshStandardMaterial color="#555555" />
          </Box>
          
          {/* Index Finger */}
          <Box args={[0.025, 0.08, 0.025]} position={[-0.02, -0.95, 0.08]}>
            <meshStandardMaterial color="#555555" />
          </Box>
          
          {/* Middle Finger */}
          <Box args={[0.025, 0.09, 0.025]} position={[0, -0.95, 0.08]}>
            <meshStandardMaterial color="#555555" />
          </Box>
          
          {/* Ring Finger */}
          <Box args={[0.025, 0.08, 0.025]} position={[0.02, -0.95, 0.08]}>
            <meshStandardMaterial color="#555555" />
          </Box>
          
          {/* Pinky */}
          <Box args={[0.02, 0.07, 0.02]} position={[0.04, -0.95, 0.08]}>
            <meshStandardMaterial color="#555555" />
          </Box>
        </group>
      </group>
      
      {/* Right Shoulder Joint */}
      <group ref={rightShoulderRef} position={[0.4, 0.3, 0]}>
        <Sphere args={[0.1, 8, 8]} position={[0, 0, 0]}>
          <meshStandardMaterial color="#333333" metalness={0.9} roughness={0.1} />
        </Sphere>
        
        {/* Right Arm with articulated movement */}
        <group ref={rightArmRef} position={[0, -0.2, 0]}>
          {/* Upper Arm - More cylindrical */}
          <Cylinder args={[0.08, 0.08, 0.5]} position={[0, -0.25, 0]} rotation={[0, 0, Math.PI/2]}>
            <meshStandardMaterial color={color} metalness={0.7} roughness={0.3} />
          </Cylinder>
          
          {/* Arm Details */}
          <Box args={[0.1, 0.3, 0.05]} position={[0, -0.1, 0.08]}>
            <meshStandardMaterial color="#444444" metalness={0.8} roughness={0.2} />
          </Box>
          
          {/* Elbow Joint */}
          <Sphere args={[0.1, 12, 12]} position={[0, -0.5, 0]}>
            <meshStandardMaterial color="#333333" metalness={0.9} roughness={0.1} />
          </Sphere>
          
          {/* Forearm - More cylindrical */}
          <Cylinder args={[0.06, 0.06, 0.4]} position={[0, -0.7, 0]} rotation={[0, 0, Math.PI/2]}>
            <meshStandardMaterial color={color} metalness={0.7} roughness={0.3} />
          </Cylinder>
          
          {/* Wrist Joint */}
          <Sphere args={[0.08, 8, 8]} position={[0, -0.9, 0]}>
            <meshStandardMaterial color="#333333" metalness={0.9} roughness={0.1} />
          </Sphere>
          
          {/* Hand - More detailed with proper fingers */}
          <Box args={[0.1, 0.15, 0.2]} position={[0, -1.0, 0]}>
            <meshStandardMaterial color="#666666" metalness={0.8} roughness={0.2} />
          </Box>
          
          {/* Thumb */}
          <Box args={[0.03, 0.06, 0.03]} position={[-0.05, -0.95, 0.05]} rotation={[0, 0, Math.PI/4]}>
            <meshStandardMaterial color="#555555" />
          </Box>
          
          {/* Index Finger */}
          <Box args={[0.025, 0.08, 0.025]} position={[-0.02, -0.95, 0.08]}>
            <meshStandardMaterial color="#555555" />
          </Box>
          
          {/* Middle Finger */}
          <Box args={[0.025, 0.09, 0.025]} position={[0, -0.95, 0.08]}>
            <meshStandardMaterial color="#555555" />
          </Box>
          
          {/* Ring Finger */}
          <Box args={[0.025, 0.08, 0.025]} position={[0.02, -0.95, 0.08]}>
            <meshStandardMaterial color="#555555" />
          </Box>
          
          {/* Pinky */}
          <Box args={[0.02, 0.07, 0.02]} position={[0.04, -0.95, 0.08]}>
            <meshStandardMaterial color="#555555" />
          </Box>
        </group>
      </group>
      
      {/* Left Hip Joint */}
      <group ref={leftHipRef} position={[-0.2, -0.6, 0]}>
        <Sphere args={[0.1, 8, 8]} position={[0, 0, 0]}>
          <meshStandardMaterial color="#333333" metalness={0.9} roughness={0.1} />
        </Sphere>
        
        {/* Left Leg with articulated movement */}
        <group ref={leftLegRef} position={[0, -0.3, 0]}>
          {/* Thigh - More cylindrical */}
          <Cylinder args={[0.1, 0.1, 0.5]} position={[0, -0.25, 0]} rotation={[0, 0, 0]}>
            <meshStandardMaterial color={color} metalness={0.7} roughness={0.3} />
          </Cylinder>
          
          {/* Thigh Details */}
          <Box args={[0.12, 0.4, 0.05]} position={[0, -0.1, 0.1]}>
            <meshStandardMaterial color="#444444" metalness={0.8} roughness={0.2} />
          </Box>
          
          {/* Knee Joint */}
          <Sphere args={[0.12, 12, 12]} position={[0, -0.5, 0]}>
            <meshStandardMaterial color="#333333" metalness={0.9} roughness={0.1} />
          </Sphere>
          
          {/* Shin - More cylindrical */}
          <Cylinder args={[0.08, 0.08, 0.4]} position={[0, -0.7, 0]} rotation={[0, 0, 0]}>
            <meshStandardMaterial color={color} metalness={0.7} roughness={0.3} />
          </Cylinder>
          
          {/* Ankle Joint */}
          <Sphere args={[0.08, 8, 8]} position={[0, -0.9, 0]}>
            <meshStandardMaterial color="#333333" metalness={0.9} roughness={0.1} />
          </Sphere>
          
          {/* Foot - More detailed */}
          <Box args={[0.15, 0.08, 0.25]} position={[0, -0.95, 0.1]}>
            <meshStandardMaterial color="#444444" metalness={0.8} roughness={0.2} />
          </Box>
          
          {/* Toe Details */}
          <Box args={[0.03, 0.05, 0.08]} position={[-0.05, -0.95, 0.2]}>
            <meshStandardMaterial color="#555555" />
          </Box>
          <Box args={[0.03, 0.05, 0.08]} position={[0, -0.95, 0.2]}>
            <meshStandardMaterial color="#555555" />
          </Box>
          <Box args={[0.03, 0.05, 0.08]} position={[0.05, -0.95, 0.2]}>
            <meshStandardMaterial color="#555555" />
          </Box>
        </group>
      </group>
      
      {/* Right Hip Joint */}
      <group ref={rightHipRef} position={[0.2, -0.6, 0]}>
        <Sphere args={[0.1, 8, 8]} position={[0, 0, 0]}>
          <meshStandardMaterial color="#333333" metalness={0.9} roughness={0.1} />
        </Sphere>
        
        {/* Right Leg with articulated movement */}
        <group ref={rightLegRef} position={[0, -0.3, 0]}>
          {/* Thigh - More cylindrical */}
          <Cylinder args={[0.1, 0.1, 0.5]} position={[0, -0.25, 0]} rotation={[0, 0, 0]}>
            <meshStandardMaterial color={color} metalness={0.7} roughness={0.3} />
          </Cylinder>
          
          {/* Thigh Details */}
          <Box args={[0.12, 0.4, 0.05]} position={[0, -0.1, 0.1]}>
            <meshStandardMaterial color="#444444" metalness={0.8} roughness={0.2} />
          </Box>
          
          {/* Knee Joint */}
          <Sphere args={[0.12, 12, 12]} position={[0, -0.5, 0]}>
            <meshStandardMaterial color="#333333" metalness={0.9} roughness={0.1} />
          </Sphere>
          
          {/* Shin - More cylindrical */}
          <Cylinder args={[0.08, 0.08, 0.4]} position={[0, -0.7, 0]} rotation={[0, 0, 0]}>
            <meshStandardMaterial color={color} metalness={0.7} roughness={0.3} />
          </Cylinder>
          
          {/* Ankle Joint */}
          <Sphere args={[0.08, 8, 8]} position={[0, -0.9, 0]}>
            <meshStandardMaterial color="#333333" metalness={0.9} roughness={0.1} />
          </Sphere>
          
          {/* Foot - More detailed */}
          <Box args={[0.15, 0.08, 0.25]} position={[0, -0.95, 0.1]}>
            <meshStandardMaterial color="#444444" metalness={0.8} roughness={0.2} />
          </Box>
          
          {/* Toe Details */}
          <Box args={[0.03, 0.05, 0.08]} position={[-0.05, -0.95, 0.2]}>
            <meshStandardMaterial color="#555555" />
          </Box>
          <Box args={[0.03, 0.05, 0.08]} position={[0, -0.95, 0.2]}>
            <meshStandardMaterial color="#555555" />
          </Box>
          <Box args={[0.03, 0.05, 0.08]} position={[0.05, -0.95, 0.2]}>
            <meshStandardMaterial color="#555555" />
          </Box>
        </group>
      </group>
      
      {/* Robot Name */}
      <Text
        position={[0, -2, 0]}
        fontSize={0.2}
        color="white"
        anchorX="center"
        anchorY="middle"
      >
        {name}
      </Text>
    </group>
  );
};

// Dance Floor Component
const DanceFloor: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const floorRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (floorRef.current && isActive) {
      const intensity = Math.sin(state.clock.elapsedTime * 2) * 0.4 + 1.2;
      const material = floorRef.current.material as THREE.MeshStandardMaterial;
      if (material) {
        material.emissiveIntensity = intensity;
        material.emissive.setHex(0x2a2a4e);
      }
    }
  });

  return (
    <mesh ref={floorRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.1, 0]}>
      <planeGeometry args={[20, 20]} />
      <meshStandardMaterial 
        color="#2a2a4e" 
        emissive="#1a1a3e" 
        emissiveIntensity={isActive ? 1.2 : 0.8}
        metalness={0.6}
        roughness={0.3}
      />
    </mesh>
  );
};

// Arena Lights Component
const ArenaLights: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const mainSpotRef = useRef<THREE.SpotLight>(null);
  const leftSpotRef = useRef<THREE.SpotLight>(null);
  const rightSpotRef = useRef<THREE.SpotLight>(null);

  useFrame((state) => {
    const time = state.clock.elapsedTime;
    
    // Main center spotlight
    if (mainSpotRef.current && isActive) {
      mainSpotRef.current.intensity = 3.5 + Math.sin(time * 1.5) * 0.5;
      mainSpotRef.current.target.position.set(0, 0, 0);
      mainSpotRef.current.target.updateMatrixWorld();
    }
    
    // Left spotlight for blue robot
    if (leftSpotRef.current && isActive) {
      leftSpotRef.current.intensity = 2.8 + Math.sin(time * 2) * 0.4;
      leftSpotRef.current.target.position.set(-3, 0.5, 0);
      leftSpotRef.current.target.updateMatrixWorld();
    }
    
    // Right spotlight for red robot
    if (rightSpotRef.current && isActive) {
      rightSpotRef.current.intensity = 2.8 + Math.sin(time * 2 + Math.PI) * 0.4;
      rightSpotRef.current.target.position.set(3, 0.5, 0);
      rightSpotRef.current.target.updateMatrixWorld();
    }
  });

  return (
    <>
      {/* Main center spotlight */}
      <spotLight
        ref={mainSpotRef}
        position={[0, 12, 0]}
        target-position={[0, 0, 0]}
        intensity={isActive ? 3.5 : 2.5}
        color="#ffffff"
        angle={Math.PI / 3}
        penumbra={0.1}
        distance={30}
        decay={1}
        castShadow
      />
      
      {/* Left spotlight for blue robot */}
      <spotLight
        ref={leftSpotRef}
        position={[-3, 10, 4]}
        target-position={[-3, 0.5, 0]}
        intensity={isActive ? 2.8 : 2.2}
        color="#4ecdc4"
        angle={Math.PI / 4}
        penumbra={0.1}
        distance={25}
        decay={1}
        castShadow
      />
      
      {/* Right spotlight for red robot */}
      <spotLight
        ref={rightSpotRef}
        position={[3, 10, 4]}
        target-position={[3, 0.5, 0]}
        intensity={isActive ? 2.8 : 2.2}
        color="#ff6b6b"
        angle={Math.PI / 4}
        penumbra={0.1}
        distance={25}
        decay={1}
        castShadow
      />
      
      {/* Additional bright overhead lights */}
      <pointLight position={[0, 15, 0]} intensity={2.0} color="#ffffff" distance={40} decay={1} />
      <pointLight position={[-2, 12, 0]} intensity={1.5} color="#ffffff" distance={30} decay={1} />
      <pointLight position={[2, 12, 0]} intensity={1.5} color="#ffffff" distance={30} decay={1} />
      
      {/* Rim lights for dramatic effect */}
      <pointLight position={[-8, 4, 0]} intensity={1.2} color="#00ffff" distance={20} decay={1} />
      <pointLight position={[8, 4, 0]} intensity={1.2} color="#ff0080" distance={20} decay={1} />
      <pointLight position={[0, 4, -8]} intensity={1.0} color="#45b7d1" distance={18} decay={1} />
      <pointLight position={[0, 4, 8]} intensity={1.0} color="#f9ca24" distance={18} decay={1} />
      
      {/* Side fill lights */}
      <pointLight position={[-6, 6, 0]} intensity={1.0} color="#ffffff" distance={25} decay={1} />
      <pointLight position={[6, 6, 0]} intensity={1.0} color="#ffffff" distance={25} decay={1} />
      
      {/* Ambient light for overall illumination */}
      <ambientLight intensity={0.8} color="#ffffff" />
    </>
  );
};

const RobotDanceBattle: React.FC = () => {
  const [currentRound, setCurrentRound] = useState(1);
  const [totalRounds] = useState(3);
  const [playerScore, setPlayerScore] = useState(0);
  const [opponentScore, setOpponentScore] = useState(0);
  const [gamePhase, setGamePhase] = useState<'setup' | 'playing' | 'results'>('setup');
  const [selectedMoves, setSelectedMoves] = useState<DanceMove[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentMove, setCurrentMove] = useState<DanceMove | null>(null);
  const [moveIndex, setMoveIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [isDancing, setIsDancing] = useState(false);
  const [danceAnimation, setDanceAnimation] = useState<string>('');
  const [opponentDancing, setOpponentDancing] = useState(false);
  const [opponentAnimation, setOpponentAnimation] = useState<string>('');
  const [show3DArena, setShow3DArena] = useState(false);
  const [arenaActive, setArenaActive] = useState(false);

  const [opponentRobot, setOpponentRobot] = useState<Robot>({
    id: 'opponent',
    name: 'Dance Master',
    color: 'red',
    moves: [],
    score: 0,
    isActive: true
  });

  const finishRound = useCallback(() => {
    setIsPlaying(false);
    
    // Calculate scores based on move complexity and timing
    const playerScore = selectedMoves.reduce((total, move) => total + move.duration, 0);
    const opponentScore = opponentRobot.moves.reduce((total, move) => total + move.duration, 0);
    
    setPlayerScore(playerScore);
    setOpponentScore(opponentScore);
    
    setTimeout(() => {
      if (currentRound < totalRounds) {
        setCurrentRound(currentRound + 1);
        setGamePhase('setup');
        setSelectedMoves([]);
        setMoveIndex(0);
        setCurrentMove(null);
      } else {
        setGamePhase('results');
      }
    }, 3000);
  }, [selectedMoves, opponentRobot.moves, currentRound, totalRounds]);

  const danceMoves: DanceMove[] = [
    { id: '1', name: 'Robot Wave', emoji: '👋', duration: 2, description: 'Wave hello like a robot' },
    { id: '2', name: 'Spin Attack', emoji: '🌪️', duration: 3, description: 'Spin around like a tornado' },
    { id: '3', name: 'Moonwalk', emoji: '🌙', duration: 2, description: 'Slide backwards smoothly' },
    { id: '4', name: 'Robot Shuffle', emoji: '🤖', duration: 2, description: 'Quick side-to-side steps' },
    { id: '5', name: 'Power Pose', emoji: '💪', duration: 1, description: 'Strike a powerful pose' },
    { id: '6', name: 'Breakdance', emoji: '🕺', duration: 4, description: 'Complex floor moves' },
    { id: '7', name: 'Robot Jump', emoji: '🤸', duration: 1, description: 'Jump with robot precision' },
    { id: '8', name: 'Synchronized', emoji: '🔄', duration: 3, description: 'Perfect timing moves' }
  ];

  // Dance animation functions
  const getDanceAnimation = (move: DanceMove) => {
    switch (move.id) {
      case '1': return 'robot-wave'; // Robot Wave
      case '2': return 'robot-spin'; // Spin Attack
      case '3': return 'robot-moonwalk'; // Moonwalk
      case '4': return 'robot-shuffle'; // Robot Shuffle
      case '5': return 'robot-wave'; // Power Pose (use wave for dramatic arm movement)
      case '6': return 'robot-breakdance'; // Breakdance
      case '7': return 'robot-jump'; // Robot Jump
      case '8': return 'robot-spin'; // Synchronized (use spin for coordinated movement)
      default: return 'robot-wave';
    }
  };

  const getOpponentAnimation = () => {
    const animations = ['robot-wave', 'robot-spin', 'robot-shuffle', 'robot-jump', 'robot-moonwalk', 'robot-breakdance'];
    return animations[Math.floor(Math.random() * animations.length)];
  };

  // const [playerRobot, setPlayerRobot] = useState<Robot>({
  //   id: 'player',
  //   name: 'Your Robot',
  //   color: 'blue',
  //   moves: [],
  //   score: 0,
  //   isActive: true
  // });

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0 && isPlaying) {
      finishRound();
    }
    return () => clearInterval(interval);
  }, [isPlaying, timeLeft, finishRound]);

  // Move execution effect
  useEffect(() => {
    if (isPlaying && selectedMoves.length > 0 && moveIndex < selectedMoves.length) {
      const move = selectedMoves[moveIndex];
      setCurrentMove(move);
      setIsDancing(true);
      setDanceAnimation(getDanceAnimation(move));
      
      const moveTimer = setTimeout(() => {
        setMoveIndex(moveIndex + 1);
        setCurrentMove(null);
        setIsDancing(false);
        setDanceAnimation('');
      }, move.duration * 1000);

      return () => clearTimeout(moveTimer);
    }
  }, [isPlaying, selectedMoves, moveIndex]);

  // Opponent dancing effect
  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        setOpponentDancing(true);
        setOpponentAnimation(getOpponentAnimation());
        
        setTimeout(() => {
          setOpponentDancing(false);
          setOpponentAnimation('');
        }, 1000);
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [isPlaying]);

  const addMoveToSequence = (move: DanceMove) => {
    if (selectedMoves.length < 8) {
      setSelectedMoves([...selectedMoves, move]);
    }
  };

  const removeMoveFromSequence = (index: number) => {
    const newMoves = selectedMoves.filter((_, i) => i !== index);
    setSelectedMoves(newMoves);
  };

  const generateOpponentMoves = () => {
    const shuffled = [...danceMoves].sort(() => 0.5 - Math.random());
    const opponentMoves = shuffled.slice(0, Math.floor(Math.random() * 5) + 3);
    setOpponentRobot(prev => ({ ...prev, moves: opponentMoves }));
  };

  const startGame = () => {
    if (selectedMoves.length === 0) {
      alert('Please select at least one dance move!');
      return;
    }
    
    generateOpponentMoves();
    setGamePhase('playing');
    setIsPlaying(true);
    setTimeLeft(30);
    setMoveIndex(0);
    setShow3DArena(true);
    setArenaActive(true);
  };

  const resetGame = () => {
    setCurrentRound(1);
    setPlayerScore(0);
    setOpponentScore(0);
    setGamePhase('setup');
    setSelectedMoves([]);
    setIsPlaying(false);
    setCurrentMove(null);
    setMoveIndex(0);
    setTimeLeft(30);
  };

  const getGameResult = () => {
    if (playerScore > opponentScore) {
      return { winner: 'You Win!', message: 'Amazing dance moves! Your robot is the champion!', color: 'text-green-600' };
    } else if (opponentScore > playerScore) {
      return { winner: 'Dance Master Wins!', message: 'Better luck next time! Keep practicing!', color: 'text-red-600' };
    } else {
      return { winner: 'It\'s a Tie!', message: 'What an incredible dance battle!', color: 'text-yellow-600' };
    }
  };

  return (
    <>
      {/* 3D Arena - Shows when game is playing */}
      {show3DArena && (
        <div className="fixed inset-0 z-50 bg-black">
          <Canvas 
            camera={{ position: [0, 5, 10], fov: 60 }}
            style={{ width: '100%', height: '100%' }}
            gl={{ antialias: true, alpha: false }}
          >
            <ArenaLights isActive={arenaActive} />
            <DanceFloor isActive={arenaActive} />
            
            {/* Player Robot */}
            <Robot3D
              position={[-3, 2, 0]}
              color="#4a90e2"
              isDancing={isDancing}
              danceAnimation={danceAnimation}
              name="Your Robot"
            />
            
            {/* Opponent Robot */}
            <Robot3D
              position={[3, 2, 0]}
              color="#e24a4a"
              isDancing={opponentDancing}
              danceAnimation={opponentAnimation}
              name="Dance Master"
            />
            
            <OrbitControls enablePan={true} enableZoom={true} enableRotate={true} />
          </Canvas>
          
          {/* 3D Arena Controls */}
          <div className="absolute top-4 left-4 right-4 z-10">
            <div className="bg-black/70 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">🤖 3D Dance Arena</h2>
                <button
                  onClick={() => setShow3DArena(false)}
                  className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-sm"
                >
                  Exit 3D Arena
                </button>
              </div>
              
              {/* Game Status */}
              <div className="flex justify-between items-center text-sm">
                <div>
                  <span className="text-blue-400">Your Robot: </span>
                  <span className={isDancing ? 'text-green-400' : 'text-gray-400'}>
                    {isDancing ? 'Dancing!' : 'Ready'}
                  </span>
                </div>
                <div>
                  <span className="text-red-400">Dance Master: </span>
                  <span className={opponentDancing ? 'text-green-400' : 'text-gray-400'}>
                    {opponentDancing ? 'Dancing!' : 'Ready'}
                  </span>
                </div>
                <div>
                  <span className="text-yellow-400">Round: </span>
                  <span className="text-white">{currentRound}/{totalRounds}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Instructions */}
          <div className="absolute bottom-4 left-4 right-4 z-10">
            <div className="bg-black/70 backdrop-blur-sm rounded-lg p-4 text-white text-center">
              <p className="text-sm">
                🎮 Watch your robots dance in 3D! 
                <br />
                🎯 Use mouse to rotate, zoom, and pan the arena
              </p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes robotWave {
          0%, 100% { transform: rotate(0deg) scale(1); }
          25% { transform: rotate(-10deg) scale(1.1); }
          50% { transform: rotate(10deg) scale(1.2); }
          75% { transform: rotate(-5deg) scale(1.1); }
        }
        
        @keyframes robotSpin {
          0% { transform: rotate(0deg) scale(1); }
          50% { transform: rotate(180deg) scale(1.2); }
          100% { transform: rotate(360deg) scale(1); }
        }
        
        @keyframes robotShuffle {
          0%, 100% { transform: translateX(0px) scale(1); }
          25% { transform: translateX(-10px) scale(1.1); }
          50% { transform: translateX(10px) scale(1.2); }
          75% { transform: translateX(-5px) scale(1.1); }
        }
        
        @keyframes robotJump {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-20px) scale(1.3); }
        }
        
        @keyframes danceGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.5); }
          50% { box-shadow: 0 0 40px rgba(59, 130, 246, 0.8), 0 0 60px rgba(147, 51, 234, 0.6); }
        }
        
        .robot-wave { animation: robotWave 0.8s ease-in-out; }
        .robot-spin { animation: robotSpin 1s linear; }
        .robot-shuffle { animation: robotShuffle 0.6s ease-in-out; }
        .robot-jump { animation: robotJump 0.5s ease-in-out; }
        .dance-glow { animation: danceGlow 1s ease-in-out infinite; }
      `}</style>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              🤖 Robot Dance Battle 🕺
            </h1>
            <p className="text-xl md:text-2xl text-purple-100">
              Program your robot to dance and compete in epic battles!
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {gamePhase === 'setup' && (
          <div className="space-y-8">
            {/* Game Info */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Round {currentRound} of {totalRounds}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Your Robot</h3>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white text-2xl">
                      🤖
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Your Robot</p>
                      <p className="text-sm text-gray-600">Score: {playerScore}</p>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Opponent</h3>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center text-white text-2xl">
                      🤖
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Dance Master</p>
                      <p className="text-sm text-gray-600">Score: {opponentScore}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Dance Moves Selection */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Choose Your Dance Moves</h3>
              <p className="text-gray-600 mb-6">Select up to 8 dance moves to create your routine:</p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {danceMoves.map((move) => (
                  <button
                    key={move.id}
                    onClick={() => addMoveToSequence(move)}
                    disabled={selectedMoves.length >= 8}
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="text-3xl mb-2">{move.emoji}</div>
                    <div className="font-semibold text-sm">{move.name}</div>
                    <div className="text-xs text-gray-500">{move.duration}s</div>
                  </button>
                ))}
              </div>

              {/* Selected Moves */}
              {selectedMoves.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Your Dance Sequence:</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedMoves.map((move, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-2 bg-purple-100 text-purple-800 px-3 py-2 rounded-lg"
                      >
                        <span className="text-lg">{move.emoji}</span>
                        <span className="font-medium">{move.name}</span>
                        <button
                          onClick={() => removeMoveFromSequence(index)}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={startGame}
                  disabled={selectedMoves.length === 0}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  🎵 Start Dance Battle!
                </button>
                <button
                  onClick={() => setShow3DArena(!show3DArena)}
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300"
                >
                  🤖 {show3DArena ? 'Hide' : 'Show'} 3D Arena
                </button>
              </div>
            </div>
          </div>
        )}

        {gamePhase === 'playing' && (
          <div className="space-y-8">
            {/* Game Timer */}
            <div className="bg-white rounded-xl shadow-lg p-6 text-center">
              <div className="text-6xl font-bold text-purple-600 mb-4">{timeLeft}</div>
              <p className="text-xl text-gray-600">seconds remaining</p>
            </div>

            {/* Dance Arena */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">Dance Arena</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Player Robot */}
                <div className="text-center">
                  <h4 className="text-lg font-semibold text-blue-600 mb-4">Your Robot</h4>
                  <div className={`w-32 h-32 bg-blue-100 rounded-full flex items-center justify-center text-6xl mx-auto mb-4 transition-all duration-300 ${
                    isDancing ? danceAnimation : ''
                  } ${isDancing ? 'bg-blue-200 shadow-lg scale-110 dance-glow' : 'bg-blue-100'}`}>
                    {currentMove && moveIndex < selectedMoves.length ? currentMove.emoji : '🤖'}
                  </div>
                  <p className="text-sm text-gray-600">
                    {currentMove && moveIndex < selectedMoves.length ? currentMove.name : 'Ready to dance!'}
                  </p>
                  {isDancing && (
                    <div className="mt-2">
                      <div className="flex justify-center space-x-1">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Opponent Robot */}
                <div className="text-center">
                  <h4 className="text-lg font-semibold text-red-600 mb-4">Dance Master</h4>
                  <div className={`w-32 h-32 bg-red-100 rounded-full flex items-center justify-center text-6xl mx-auto mb-4 transition-all duration-300 ${
                    opponentDancing ? opponentAnimation : ''
                  } ${opponentDancing ? 'bg-red-200 shadow-lg scale-110 dance-glow' : 'bg-red-100'}`}>
                    🤖
                  </div>
                  <p className="text-sm text-gray-600">
                    {opponentDancing ? 'Dancing like a pro!' : 'Dancing automatically...'}
                  </p>
                  {opponentDancing && (
                    <div className="mt-2">
                      <div className="flex justify-center space-x-1">
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Dance Floor Effects */}
              {isPlaying && (
                <div className="mt-8">
                  <div className="flex justify-center space-x-4">
                    <div className="w-4 h-4 bg-purple-400 rounded-full animate-ping"></div>
                    <div className="w-4 h-4 bg-pink-400 rounded-full animate-ping" style={{animationDelay: '0.2s'}}></div>
                    <div className="w-4 h-4 bg-blue-400 rounded-full animate-ping" style={{animationDelay: '0.4s'}}></div>
                    <div className="w-4 h-4 bg-green-400 rounded-full animate-ping" style={{animationDelay: '0.6s'}}></div>
                    <div className="w-4 h-4 bg-yellow-400 rounded-full animate-ping" style={{animationDelay: '0.8s'}}></div>
                  </div>
                  <p className="text-center text-sm text-gray-500 mt-2">🎵 Dance Floor Active 🎵</p>
                </div>
              )}
            </div>

            {/* Progress */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Dance Progress</h4>
              <div className="space-y-2">
                {selectedMoves.map((move, index) => (
                  <div
                    key={index}
                    className={`flex items-center space-x-3 p-2 rounded-lg ${
                      index < moveIndex ? 'bg-green-100 text-green-800' :
                      index === moveIndex ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-600'
                    }`}
                  >
                    <span className="text-lg">{move.emoji}</span>
                    <span className="font-medium">{move.name}</span>
                    <span className="text-sm">({move.duration}s)</span>
                    {index < moveIndex && <span className="text-green-600">✓</span>}
                    {index === moveIndex && <span className="text-yellow-600">→</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {gamePhase === 'results' && (
          <div className="space-y-8">
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Battle Results!</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-blue-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-blue-600 mb-2">Your Score</h3>
                  <div className="text-4xl font-bold text-blue-600">{playerScore}</div>
                  <p className="text-sm text-gray-600">points</p>
                </div>
                <div className="bg-red-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-red-600 mb-2">Dance Master</h3>
                  <div className="text-4xl font-bold text-red-600">{opponentScore}</div>
                  <p className="text-sm text-gray-600">points</p>
                </div>
              </div>

              <div className="mb-8">
                <h3 className={`text-2xl font-bold mb-2 ${getGameResult().color}`}>
                  {getGameResult().winner}
                </h3>
                <p className="text-lg text-gray-600">{getGameResult().message}</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={resetGame}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300"
                >
                  🎵 Play Again
                </button>
                <Link
                  to="/high-code/games"
                  className="bg-gray-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-700 transition-colors text-center"
                >
                  Back to Games
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default RobotDanceBattle;
